import { alpha, createTheme, type PaletteMode } from '@mui/material/styles';

export const layoutTokens = {
  headerHeight: 72,
  controlHeight: 48,
  panelRadius: 16,
  controlRadius: 12,
  panelBlur: 14,
  contentWidth: 1120
} as const;

export const themeStorageKey = 'keep.theme';

const brand = '#4550E6';
const brandDark = '#21276E';
const darkAccent = '#FBBF24';

export function createArchiveTheme(mode: PaletteMode) {
  const dark = mode === 'dark';
  const primary = dark ? darkAccent : brand;
  const primaryHover = dark ? '#F59E0B' : brandDark;
  const surface = dark ? 'rgba(255, 255, 255, 0.10)' : 'rgba(255, 255, 255, 0.85)';
  const border = dark ? 'rgba(255, 255, 255, 0.20)' : 'rgba(15, 23, 42, 0.12)';

  return createTheme({
    palette: {
      mode,
      primary: { main: primary, dark: primaryHover, contrastText: dark ? '#0F172A' : '#FFFFFF' },
      secondary: { main: dark ? '#35C391' : '#00C568' },
      background: {
        default: dark ? '#020617' : '#F7F9FF',
        paper: surface
      },
      text: {
        primary: dark ? '#FFFFFF' : '#171A2B',
        secondary: dark ? 'rgba(255, 255, 255, 0.68)' : '#656B7C'
      },
      divider: border,
      warning: { main: dark ? '#FBBF24' : '#FFA047', light: dark ? '#6B5315' : '#FFF0BF' }
    },
    typography: {
      fontFamily: '"Source Sans 3", "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h1: { fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.04, fontWeight: 700, letterSpacing: '-0.035em' },
      h2: { fontSize: '1.25rem', lineHeight: 1.25, fontWeight: 700, letterSpacing: '-0.015em' },
      h3: { fontSize: '1rem', lineHeight: 1.35, fontWeight: 700 },
      button: { fontWeight: 650, textTransform: 'none' }
    },
    shape: { borderRadius: layoutTokens.panelRadius },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': { colorScheme: mode },
          body: {
            color: dark ? '#FFFFFF' : '#171A2B',
            background: dark
              ? 'linear-gradient(180deg, #2563EB 0%, #020617 100%)'
              : 'linear-gradient(160deg, #FBFCFF 0%, #F3F7FD 52%, #EAF1FB 100%)',
            backgroundAttachment: 'fixed'
          },
          '::selection': { backgroundColor: alpha(primary, dark ? 0.34 : 0.2) }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backdropFilter: `blur(${layoutTokens.panelBlur}px)`,
            WebkitBackdropFilter: `blur(${layoutTokens.panelBlur}px)`
          }
        }
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { minHeight: 42, borderRadius: layoutTokens.controlRadius, paddingInline: 18 },
          containedPrimary: { '&:hover': { backgroundColor: primaryHover } }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: dark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(69, 80, 230, 0.08)' }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            minHeight: layoutTokens.controlHeight,
            borderRadius: layoutTokens.controlRadius,
            backgroundColor: dark ? 'rgba(0, 0, 0, 0.20)' : alpha('#FFFFFF', 0.82),
            '& .MuiOutlinedInput-notchedOutline': { borderColor: dark ? 'rgba(255, 255, 255, 0.24)' : 'rgba(15, 23, 42, 0.16)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(primary, 0.72) }
          }
        }
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 9, fontWeight: 600 } } },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${border}`,
            boxShadow: dark ? '0 12px 36px rgba(2, 6, 23, 0.24)' : '0 8px 30px rgba(22, 34, 80, 0.06)'
          }
        }
      }
    }
  });
}
